import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  ChartPieIcon,
  TagIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowUpRightIcon,
  ArrowDownRightIcon,
  CalendarIcon,
  BuildingLibraryIcon,
  BanknotesIcon,
  HeartIcon,
  CreditCardIcon,
  WalletIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  DocumentArrowDownIcon,
  DocumentIcon,
  PlusIcon,
  UserIcon,
  UserPlusIcon,
  UsersIcon,
  CurrencyEuroIcon,
  TrashIcon,
  ArrowUturnLeftIcon,
  PencilIcon,
  GiftIcon,
  ShoppingCartIcon,
  HomeIcon,
  TruckIcon,
  BriefcaseIcon,
  QuestionMarkCircleIcon,
  ChartBarIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon,
  EllipsisHorizontalIcon,
  BoltIcon,
  BookOpenIcon,
  ShieldCheckIcon,
  BeakerIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import ExcelExportModal from '../components/ExcelExportModal';
import CSVImporter from '../components/CSVImporter';
import ConfirmationModal from '../components/ConfirmationModal';
import AddBookingModal from '../components/AddBookingModal';
import StatementDetailModal from '../components/modals/StatementDetailModal';
import BankStatementManager from '../components/BankStatementManager';
import UncategorizedReviewModal from '../components/modals/UncategorizedReviewModal';
import RuecklagenCard from '../components/RuecklagenCard';
import AttributeLabel from '../components/AttributeLabel';
import { StatCard } from '../components/StatCard';
import { ModalShell, ModalActions, ModalSection } from '../components/modals/core';
import useFinanceDesignContext from '../hooks/useFinanceDesignContext';

interface Account {
  id: number;
  name: string;
  kontotyp: string;
  beschreibung: string;
  waehrung: string;
  startbetrag: number;
  deactivated: number;
  icon: string;
  ist_gaestekarten_konto?: number;
  ist_verzehrkosten_konto?: number;
  erstellt_am?: string;
  currentBalance?: number;
  recentTransactions?: Transaction[];
}

interface Category {
  category: string;
  income: number;
  expense: number;
  transactionCount: number;
  icon: string | null;
}

interface Transaction {
  id: number;
  buchungstag: string;
  /** Buchungsart der Bank („GUTSCHRIFT", „AUSZAHLUNG", …) — meist wenig aussagekräftig. */
  buchungstext: string;
  /** Wer gezahlt bzw. wer das Geld bekommen hat. */
  name_zahlungsbeteiligter?: string | null;
  verwendungszweck?: string | null;
  betrag: number;
  categoryAmount?: number;
  isIncome?: boolean;
  konto_name?: string;
  category?: string;
}

/**
 * Überschrift einer Buchung.
 *
 * Bewusst der Zahlungsbeteiligte und nicht `buchungstext`: letzterer ist die
 * Buchungsart der Bank und bei zwei Dritteln aller Buchungen identisch
 * („GUTSCHRIFT" allein 582 von 1197). Als Überschrift sagt er nichts darüber,
 * worum es ging. Fehlt der Beteiligte (28 von 1197, meist Kontoabschlüsse),
 * bleibt die Buchungsart als Rückfall.
 */
const zahlungsbeteiligter = (txn: Transaction): string =>
  (txn.name_zahlungsbeteiligter || '').trim() || txn.buchungstext || 'Ohne Angabe';

/**
 * Technischen SEPA-Anhang aus dem Verwendungszweck entfernen.
 *
 * Bankdaten hängen hinter dem lesbaren Text noch EREF/MREF/CRED samt IBAN und
 * BIC an — bei „Abschlag (Strom) Juni" sind das über 150 Zeichen Rauschen, die
 * in der Zeile den eigentlichen Zweck verdrängen. Der vollständige Text bleibt
 * im title-Attribut und in der Detailansicht erhalten.
 *
 * Rückfall auf den Originaltext, falls davor nichts steht (in den Echtdaten
 * genau ein Fall von 137).
 */
const SEPA_ANHANG = /\s(?:EREF|MREF|CRED|SVWZ|ABWA|ABWE)[+:].*/i;

const zweckKurz = (zweck?: string | null): string => {
  const voll = (zweck || '').trim();
  if (!voll) return '';
  return voll.replace(SEPA_ANHANG, '').trim() || voll;
};

/**
 * Die drei Textfelder einer Buchung so aufbereiten, dass nichts doppelt steht.
 *
 * Bei handisch erfassten Buchungen sind `buchungstext` und `verwendungszweck`
 * regelmäßig wortgleich („Zuviel Bargeld im Vereinsheim"), bei Bankimporten
 * unterscheiden sie sich. Ohne Abgleich stünde derselbe Satz zweimal
 * untereinander.
 */
const buchungsTexte = (txn: Transaction) => {
  const kopf = zahlungsbeteiligter(txn);
  const gleich = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

  const zweck = zweckKurz(txn.verwendungszweck);
  const zeigeZweck = zweck && !gleich(zweck, kopf) ? zweck : '';

  const art = (txn.buchungstext || '').trim();
  const zeigeArt = art && !gleich(art, kopf) && !gleich(art, zeigeZweck) ? art : '';

  return { kopf, zweck: zeigeZweck, art: zeigeArt };
};

interface CategoryTransactions {
  [key: string]: number[];
}

// Add CSS animation styles
const animationStyles = `
  @keyframes slideInFromBottom {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const AccountOverviewEnhanced: React.FC = () => {
  useFinanceDesignContext();
  const { user } = useContext(AuthContext);
  const currentYear = new Date().getFullYear();

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [accountDetailTab, setAccountDetailTab] = useState<'transactions' | 'statements'>('transactions');
  const [accountStatementCount, setAccountStatementCount] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [categoryTransactions, setCategoryTransactions] = useState<CategoryTransactions>({});
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);
  const [selectedStatementId, setSelectedStatementId] = useState<string | null>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([currentYear]);
  const [allStatements, setAllStatements] = useState<any[]>([]);

  // Use availableYears from API instead of calculating from allStatements
  const years = useMemo(() => {
    return availableYears.length > 0 ? availableYears : [currentYear];
  }, [availableYears, currentYear]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'warning' | 'error' | 'question' | 'info';
    title: string;
    message: string;
    variant?: 'single' | 'two';
    onConfirm?: () => void;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [showEditAccountModal, setShowEditAccountModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountBalance, setNewAccountBalance] = useState('');
  const [newAccountType, setNewAccountType] = useState('Bargeldkonto');
  const [newAccountIcon, setNewAccountIcon] = useState('FaWallet');
  const [newAccountIsGaestekarten, setNewAccountIsGaestekarten] = useState(false);
  const [newAccountIsVerzehrkosten, setNewAccountIsVerzehrkosten] = useState(false);

  // Available icons for accounts
  const availableIcons = [
    { id: 'FaLandmark', name: 'Bank', IconComponent: BuildingLibraryIcon, color: 'green' },
    { id: 'FaWallet', name: 'Portemonnaie', IconComponent: WalletIcon, color: 'red' },
    { id: 'FaCreditCard', name: 'Kreditkarte', IconComponent: CreditCardIcon, color: 'green' },
    { id: 'FaPiggyBank', name: 'Sparkonto', IconComponent: BanknotesIcon, color: 'red' },
    { id: 'FaChartLine', name: 'Depot', IconComponent: ChartBarIcon, color: 'green' },
    { id: 'FaFileInvoiceDollar', name: 'Rechnungen', IconComponent: DocumentTextIcon, color: 'green' },
    { id: 'FaHome', name: 'Zuhause', IconComponent: HomeIcon, color: 'green' },
    { id: 'FaCar', name: 'Auto', IconComponent: TruckIcon, color: 'red' },
    { id: 'FaShoppingCart', name: 'Einkaufen', IconComponent: ShoppingCartIcon, color: 'red' },
    { id: 'FaBriefcase', name: 'Geschäftlich', IconComponent: BriefcaseIcon, color: 'green' },
    { id: 'FaGift', name: 'Geschenke', IconComponent: GiftIcon, color: 'red' },
    { id: 'FaUtensils', name: 'Essen/Kantine', IconComponent: ShoppingCartIcon, color: 'green' },
    { id: 'FaUsers', name: 'Gruppe/Gäste', IconComponent: UserIcon, color: 'green' },
    { id: 'FaStore', name: 'Laden/Kasse', IconComponent: ShoppingCartIcon, color: 'red' },
    { id: 'FaQuestionCircle', name: 'Sonstiges', IconComponent: QuestionMarkCircleIcon, color: 'gray' },
  ];

  // Icon mapping for accounts
  const accountIconMap: { [key: string]: React.ComponentType<any> } = {
    FaUniversity: BuildingLibraryIcon,
    FaLandmark: BuildingLibraryIcon,
    FaUtensils: ShoppingCartIcon,
    FaHeart: HeartIcon,
    FaWallet: WalletIcon,
    FaCreditCard: CreditCardIcon,
    FaPiggyBank: BanknotesIcon,
    FaChartLine: ChartBarIcon,
    FaFileInvoiceDollar: DocumentTextIcon,
    FaHome: HomeIcon,
    FaCar: TruckIcon,
    FaShoppingCart: ShoppingCartIcon,
    FaBriefcase: BriefcaseIcon,
    FaGift: GiftIcon,
    FaUsers: UserIcon,
    FaStore: ShoppingCartIcon,
    FaQuestionCircle: QuestionMarkCircleIcon,
  };

  // OPTIMIZED: Fetch all data in one API call
  const fetchAllData = async () => {
    try {
      setIsLoading(true);
      setIsLoadingAccounts(true);

      const response = await apiClient.get(`/api/finance/account-overview-data?year=${selectedYear}`);

      if (response.data.success) {
        // Set accounts with currentBalance and recentTransactions
        setAccounts(response.data.accounts);

        // Set categories
        setCategories(response.data.categories);

        // Set available years
        setAvailableYears(response.data.availableYears);

        setError(null);
      }
    } catch (err) {
      console.error('Error fetching account overview data:', err);
      setError('Fehler beim Laden der Daten');
    } finally {
      setIsLoading(false);
      setIsLoadingAccounts(false);
    }
  };

  // Fallback: Fetch all statements for export (only when needed)
  const fetchAllStatements = async () => {
    try {
      if (accounts.length === 0) {
        console.log('No accounts available yet');
        return;
      }

      // Fetch statements for each account (like in original AccountStatementsList)
      const statementsWithOverview = await Promise.all(
        accounts.map(async (acc) => {
          try {
            const resStatements = await apiClient.get(`/api/finance/statements?account_id=${acc.id}`);
            const statementsData = resStatements.data || [];

            // Add start statement (Eröffnungsbetrag) like in original
            const startStatement = {
              id: `${acc.id}-start`,
              originalId: 'start',
              buchungstag: acc.erstellt_am ? new Date(acc.erstellt_am).toISOString().split('T')[0] : '1970-01-01',
              buchungstext: 'Eröffnungsbetrag',
              verwendungszweck: 'Kontostand zu Beginn',
              name_zahlungsbeteiligter: '',
              betrag: acc.startbetrag || 0,
              konto_id: acc.id,
              kontotyp: acc.kontotyp || 'Sonstiges',
              documents: [],
              categories: [],
            };

            // Enrich statements with additional data
            const enrichedStatements = [
              startStatement,
              ...statementsData.map((stmt: any) => ({
                ...stmt,
                originalId: stmt.id?.toString() || stmt.id,
                id: `${acc.id}-${stmt.id}`,
                konto_id: acc.id,
                kontotyp: acc.kontotyp || 'Sonstiges',
                documents: stmt.documents || [],
                name_zahlungsbeteiligter: stmt.name_zahlungsbeteiligter || '',
                verwendungszweck: stmt.verwendungszweck || '',
                buchungstext: stmt.buchungstext || '',
                betrag: parseFloat(stmt.betrag) || 0,
              })),
            ];

            return enrichedStatements;
          } catch (err) {
            console.error(`Error fetching statements for account ${acc.id}:`, err);
            return [];
          }
        })
      );

      const allStatementsFlat = statementsWithOverview.flat();
      const sorted = allStatementsFlat.sort((a, b) => new Date(b.buchungstag).getTime() - new Date(a.buchungstag).getTime());
      setAllStatements(sorted);
    } catch (error) {
      console.error('Error fetching all statements:', error);
    }
  };

  // Handle account functions
  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAccountName.trim()) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Kontoname ist erforderlich.',
        variant: 'single'
      });
      return;
    }
    try {
      const response = await apiClient.post('/api/finance/accounts', {
        name: newAccountName.trim(),
        startbetrag: parseFloat(newAccountBalance) || 0,
        kontotyp: newAccountType,
        icon: newAccountIcon,
        beschreibung: '',
        waehrung: 'EUR',
        ist_gaestekarten_konto: newAccountIsGaestekarten,
        ist_verzehrkosten_konto: newAccountIsVerzehrkosten
      });
      await fetchAllData();
      setShowCreateAccountModal(false);
      setNewAccountName('');
      setNewAccountBalance('');
      setNewAccountType('Bargeldkonto');
      setNewAccountIcon('FaWallet');
      setNewAccountIsGaestekarten(false);
      setNewAccountIsVerzehrkosten(false);
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolg',
        message: 'Konto wurde erfolgreich erstellt.',
        variant: 'single'
      });
    } catch (error) {
      console.error('Fehler beim Erstellen des Kontos:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Konto konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
        variant: 'single'
      });
    }
  };

  const handleDeleteAccount = async (accountId: number) => {
    try {
      await apiClient.delete(`/api/finance/accounts/${accountId}`);
      await fetchAllData();
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolg',
        message: 'Konto wurde erfolgreich gelöscht.',
        variant: 'single'
      });
    } catch (error) {
      console.error('Fehler beim Löschen des Kontos:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Konto konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.',
        variant: 'single'
      });
    }
  };

  const handleEditAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAccount) return;

    try {
      await apiClient.put(`/api/finance/accounts/${editingAccount.id}`, {
        name: newAccountName,
        kontotyp: newAccountType,
        beschreibung: editingAccount.beschreibung,
        waehrung: editingAccount.waehrung,
        startbetrag: editingAccount.startbetrag,
        icon: newAccountIcon,
        ist_gaestekarten_konto: newAccountIsGaestekarten ? 1 : 0,
        ist_verzehrkosten_konto: newAccountIsVerzehrkosten ? 1 : 0
      });

      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolgreich',
        message: `Konto "${newAccountName}" wurde erfolgreich aktualisiert.`,
        variant: 'single'
      });

      setShowEditAccountModal(false);
      setEditingAccount(null);
      setNewAccountName('');
      setNewAccountType('Bargeldkonto');
      setNewAccountIcon('FaWallet');
      setNewAccountIsGaestekarten(false);
      setNewAccountIsVerzehrkosten(false);
      fetchAllData();
    } catch (error) {
      console.error('Error updating account:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Konto konnte nicht aktualisiert werden.',
        variant: 'single'
      });
    }
  };

  const openEditModal = (account: Account) => {
    setEditingAccount(account);
    setNewAccountName(account.name);
    setNewAccountType(account.kontotyp);
    setNewAccountIcon(account.icon || 'FaWallet');
    setNewAccountIsGaestekarten(account.ist_gaestekarten_konto === 1);
    setNewAccountIsVerzehrkosten(account.ist_verzehrkosten_konto === 1);
    setShowEditAccountModal(true);
  };

  const handleDeactivateAccount = async (accountId: number) => {
    try {
      await apiClient.patch(`/api/finance/accounts/${accountId}`, {
        deactivated: 1
      });
      await fetchAllData();
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolg',
        message: 'Konto wurde erfolgreich deaktiviert.',
        variant: 'single'
      });
    } catch (error) {
      console.error('Fehler beim Deaktivieren des Kontos:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Konto konnte nicht deaktiviert werden. Bitte versuchen Sie es erneut.',
        variant: 'single'
      });
    }
  };

  const handleActivateAccount = async (accountId: number) => {
    try {
      await apiClient.patch(`/api/finance/accounts/${accountId}`, {
        deactivated: 0
      });
      await fetchAllData();
      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Erfolg',
        message: 'Konto wurde erfolgreich aktiviert.',
        variant: 'single'
      });
    } catch (error) {
      console.error('Fehler beim Aktivieren des Kontos:', error);
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Konto konnte nicht aktiviert werden. Bitte versuchen Sie es erneut.',
        variant: 'single'
      });
    }
  };

  // Fetch accounts
  const fetchAccounts = async () => {
    try {
      setIsLoadingAccounts(true);
      const response = await apiClient.get('/api/finance/accounts');
      
      if (Array.isArray(response.data)) {
        const accountsData = response.data;
        
        // Fetch recent transactions for each account
        const accountsWithTransactions = await Promise.all(
          accountsData.map(async (account: Account) => {
            try {
              const statementsResponse = await apiClient.get(
                `/api/finance/statements?account_id=${account.id}&limit=5`
              );
              
              let currentBalance = account.startbetrag;
              let recentTransactions: Transaction[] = [];
              
              if (statementsResponse.data && Array.isArray(statementsResponse.data)) {
                recentTransactions = statementsResponse.data.slice(0, 5);
                // Calculate current balance from all transactions
                const allTransactions = statementsResponse.data;
                currentBalance = allTransactions.reduce((sum: number, txn: any) => {
                  return sum + (parseFloat(txn.betrag) || 0);
                }, account.startbetrag);
              }
              
              return {
                ...account,
                currentBalance,
                recentTransactions,
              };
            } catch (err) {
              console.error(`Error fetching transactions for account ${account.id}:`, err);
              return {
                ...account,
                currentBalance: account.startbetrag,
                recentTransactions: [],
              };
            }
          })
        );
        
        setAccounts(accountsWithTransactions);
      }
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Fehler beim Laden der Konten');
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get(`/api/finance/account-categories?year=${selectedYear}`);
      
      if (response.data.success) {
        console.log(response.data);
        const normalizedCategories = response.data.categories.map((cat: any) => ({
          category: cat.category,
          income: parseFloat(cat.income) || 0,
          expense: Math.abs(parseFloat(cat.expense)) || 0,
          transactionCount: cat.transactionCount || 0,
          icon: cat.icon || 'TagIcon',
        }));
        setCategories(normalizedCategories);
        setError(null);
        
        // Fetch transaction timelines for all categories
        await fetchAllCategoryTransactions(normalizedCategories);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Fehler beim Laden der Kategorien');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch transaction timeline for all categories
  const fetchAllCategoryTransactions = async (categories: Category[]) => {
    setIsLoadingTransactions(true);
    try {
      const transactionPromises = categories.map(async (cat) => {
        try {
          const response = await apiClient.get(
            `/api/finance/account-categories/${encodeURIComponent(cat.category)}/${selectedYear}`
          );
          
          if (response.data.success && Array.isArray(response.data.transactions)) {
            const bookingMonths = response.data.transactions.reduce((acc: number[], txn: any) => {
              const date = new Date(txn.buchungstag);
              const month = date.getMonth();
              const monthPosition = (month / 12) * 100;
              if (!acc.includes(monthPosition)) {
                acc.push(monthPosition);
              }
              return acc;
            }, []).sort((a: number, b: number) => a - b);
            
            return { category: cat.category, bookingMonths };
          }
          return { category: cat.category, bookingMonths: [] };
        } catch (error) {
          console.error(`Error fetching transactions for ${cat.category}:`, error);
          return { category: cat.category, bookingMonths: [] };
        }
      });

      const results = await Promise.all(transactionPromises);
      const newCategoryTransactions = results.reduce((acc, { category, bookingMonths }) => {
        acc[category] = bookingMonths;
        return acc;
      }, {} as CategoryTransactions);

      setCategoryTransactions(newCategoryTransactions);
    } catch (error) {
      console.error('Error fetching transaction timelines:', error);
    } finally {
      setIsLoadingTransactions(false);
    }
  };

  // Handle category click
  const handleCategoryClick = async (category: string) => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(
        `/api/finance/account-categories/${encodeURIComponent(category)}/${selectedYear}`
      );
      
      if (response.data.success && Array.isArray(response.data.transactions)) {
        setSelectedCategory(category);
        setTransactions(response.data.transactions);
        setShowTransactionModal(true);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('Fehler beim Laden der Transaktionen');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle account click
  const handleAccountClick = async (account: Account) => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(`/api/finance/statements?account_id=${account.id}`);
      
      if (Array.isArray(response.data)) {
        setSelectedAccount(account);
        setTransactions(response.data);
        setAccountDetailTab('transactions');
        setAccountStatementCount(null);
        setShowAccountModal(true);
      }
    } catch (error) {
      console.error('Error fetching account transactions:', error);
      setError('Fehler beim Laden der Kontobuchungen');
    } finally {
      setIsLoading(false);
    }
  };

  // OPTIMIZED: Fetch all data in one API call when year changes
  useEffect(() => {
    fetchAllData();
  }, [selectedYear]);

  // Removed: Old separate fetchAccounts() + fetchCategories() calls

  // Loading state for export modal
  const [isLoadingExport, setIsLoadingExport] = useState(false);

  // Open export modal with data
  const handleOpenExportModal = async () => {
    setShowExportModal(true);
    setIsLoadingExport(true);
    await fetchAllStatements();
    setIsLoadingExport(false);
  };

  // Calculate totals
  const totalBalance = accounts.reduce((sum, acc) => sum + (acc.currentBalance || 0), 0);
  const totalIncome = categories.reduce((sum, cat) => sum + cat.income, 0);
  const totalExpense = categories.reduce((sum, cat) => sum + cat.expense, 0);
  const saldo = totalIncome - totalExpense;

  // Calculate statistics
  const transactionCount = categories.reduce((sum, cat) => sum + cat.transactionCount, 0);
  
  // Calculate uncategorized transactions
  const uncategorizedCount = categories.find(cat => 
    cat.category === 'Unkategorisiert' || 
    cat.category === 'Nicht kategorisiert' || 
    cat.category === 'Uncategorized' ||
    cat.category === ''
  )?.transactionCount || 0;
  
  const uncategorizedAmount = categories.find(cat => 
    cat.category === 'Unkategorisiert' ||
    cat.category === 'Nicht kategorisiert' || 
    cat.category === 'Uncategorized' ||
    cat.category === ''
  );
  const uncategorizedTotal = (uncategorizedAmount?.income || 0) + (uncategorizedAmount?.expense || 0);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Format currency compact for mobile
  const formatCurrencyCompact = (amount: number) => {
    const absAmount = Math.abs(amount);
    if (absAmount >= 1000) {
      return `${(amount / 1000).toFixed(1)}k`;
    } else if (absAmount >= 100) {
      return `${Math.round(amount)}€`;
    }
    return formatCurrency(amount);
  };

  // Format date
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Get account icon details
  const getAccountIconDetails = (account: Account) => {
    const defaultIconData = availableIcons.find(i => i.id === 'FaQuestionCircle') || { id: 'FaQuestionCircle', IconComponent: QuestionMarkCircleIcon, color: 'gray' };
    let iconDetail = defaultIconData;

    if (account && account.icon) {
      const foundIcon = availableIcons.find(icon => icon.id === account.icon);
      if (foundIcon) iconDetail = foundIcon;
    } else if (account && account.kontotyp) {
      const type = account.kontotyp.toLowerCase();
      if (type.includes('giro') || type.includes('bank')) iconDetail = availableIcons.find(i => i.id === 'FaLandmark') || defaultIconData;
      else if (type.includes('portemonnaie') || type.includes('bargeld') || type.includes('kasse')) iconDetail = availableIcons.find(i => i.id === 'FaWallet') || defaultIconData;
      else if (type.includes('kreditkarte')) iconDetail = availableIcons.find(i => i.id === 'FaCreditCard') || defaultIconData;
      else if (type.includes('spar')) iconDetail = availableIcons.find(i => i.id === 'FaPiggyBank') || defaultIconData;
      else if (type.includes('depot')) iconDetail = availableIcons.find(i => i.id === 'FaChartLine') || defaultIconData;
      else if (type.includes('kantine')) iconDetail = availableIcons.find(i => i.id === 'FaUtensils') || defaultIconData;
    }

    const IconComponent = iconDetail.IconComponent;
    const colorName = iconDetail.color;

    return { IconComponent, colorName };
  };

  // Category Icon Map
  const categoryIconMap: { [key: string]: any } = {
    'FaTrash': TrashIcon,
    'FaBuilding': BuildingLibraryIcon,
    'FaUsersCog': UsersIcon,
    'FaCar': TruckIcon,
    'FaUserPlus': UserPlusIcon,
    'FaDog': HeartIcon, // Using HeartIcon for dogs
    'FaTools': WrenchScrewdriverIcon,
    'FaUtensils': ShoppingCartIcon,
    'FaUsers': UsersIcon,
    'FaEllipsisH': EllipsisHorizontalIcon,
    'FaEuroSign': CurrencyEuroIcon,
    'FaHeart': HeartIcon,
    'FaBolt': BoltIcon,
    'FaBookOpen': BookOpenIcon,
    'FaShieldAlt': ShieldCheckIcon,
    'FaTint': BeakerIcon,
  };

  // Get category icon
  const getCategoryIcon = (iconName: string | null) => {
    if (!iconName) return TagIcon;
    return categoryIconMap[iconName] || TagIcon;
  };

  // Sort categories by amount (largest first)
  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => {
      const aMax = Math.max(a.income, a.expense);
      const bMax = Math.max(b.income, b.expense);
      return bMax - aMax;
    });
  }, [categories]);

  return (
    <>
      <style>{animationStyles}</style>
      <div className="finance-page max-w-7xl mx-auto">
      {/* Header - Design Spec konform */}
      <header className="app-page-header finance-page-header hidden sm:block mb-6">
        <div className="p-4 md:py-6 md:px-8 flex justify-between items-center">
          {/* Left: Title */}
          <div className="flex items-center gap-3.5">
            <span className="finance-header-icon"><BanknotesIcon /></span>
            <div>
              <p className="finance-kicker">Finanzen · Jahresblick</p>
              <h1 className="finance-title">Finanzübersicht</h1>
              <p className="finance-subtitle">Konten, Liquidität und Buchungsstatus auf einen Blick</p>
            </div>
          </div>

          {/* Right: Year Chips + Actions */}
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            {/* Year Selector as Chips - Desktop */}
            <div className="hidden 2xl:flex items-center gap-2">
              {years.map((year) => (
                <button
                  key={year}
                  onClick={() => setSelectedYear(year)}
                  className={`px-4 py-2.5 rounded-full text-[13px] font-medium transition-all border ${
                    selectedYear === year
                      ? 'bg-primary-500 border-primary-500 text-white'
                      : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white hover:border-primary-500'
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
            {/* Year Selector - Mobile */}
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="2xl:hidden w-20 h-10 px-3 rounded-xl bg-gray-50 dark:bg-[#0f1419] border-0 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {years.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            {/* Icon Buttons - Desktop only */}
            <button
              onClick={() => setShowImportModal(true)}
              className="hidden md:flex w-11 h-11 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              title="CSV Import"
            >
              <DocumentIcon className="h-5 w-5 text-gray-900 dark:text-white" />
            </button>
            <button
              onClick={handleOpenExportModal}
              className="hidden md:flex w-11 h-11 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              title="Excel Export"
            >
              <DocumentArrowDownIcon className="h-5 w-5 text-gray-900 dark:text-white" />
            </button>
            <button
              onClick={() => setIsEditMode(!isEditMode)}
              className={`w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-xl transition-colors ${
                isEditMode
                  ? 'bg-error-100 dark:bg-error-900/30 text-error-600'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              title={isEditMode ? 'Fertig' : 'Verwalten'}
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            {/* Primary Button */}
            <button
              onClick={() => setShowAddBookingModal(true)}
              className="flex items-center justify-center gap-2 w-10 h-10 md:w-auto md:h-auto md:px-5 md:py-3 rounded-xl bg-primary-600 hover:bg-primary-700 hover:shadow-lg text-white text-sm font-semibold transition-all"
              title="Neue Buchung"
            >
              <PlusIcon className="h-5 w-5" />
              <span className="hidden md:inline">Neue Buchung</span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Werkzeugleiste: die Desktop-Kopfzeile ist auf Phones bewusst
          ausgeblendet, zentrale Finanzaktionen muessen aber erreichbar bleiben. */}
      <div className="mb-4 flex items-center gap-2 sm:hidden" aria-label="Aktionen der Finanzübersicht">
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          aria-label="Finanzjahr"
          className="h-[42px] min-w-0 flex-1 rounded-xl border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-[#1c2432] dark:text-white"
        >
          {years.map((year) => <option key={year} value={year}>{year}</option>)}
        </select>
        <button
          type="button"
          onClick={() => setShowImportModal(true)}
          aria-label="CSV importieren"
          className="flex h-[42px] w-[42px] flex-none items-center justify-center rounded-xl border border-gray-300 bg-white text-gray-600 dark:border-gray-600 dark:bg-[#1c2432] dark:text-gray-300"
        >
          <DocumentIcon className="h-[19px] w-[19px]" />
        </button>
        <button
          type="button"
          onClick={handleOpenExportModal}
          aria-label="Excel exportieren"
          className="flex h-[42px] w-[42px] flex-none items-center justify-center rounded-xl border border-gray-300 bg-white text-gray-600 dark:border-gray-600 dark:bg-[#1c2432] dark:text-gray-300"
        >
          <DocumentArrowDownIcon className="h-[19px] w-[19px]" />
        </button>
        <button
          type="button"
          onClick={() => setIsEditMode(!isEditMode)}
          aria-label={isEditMode ? 'Kontoverwaltung beenden' : 'Konten verwalten'}
          className={`flex h-[42px] w-[42px] flex-none items-center justify-center rounded-xl border ${
            isEditMode
              ? 'border-error-300 bg-error-50 text-error-600 dark:border-error-800 dark:bg-error-900/25 dark:text-error-400'
              : 'border-gray-300 bg-white text-gray-600 dark:border-gray-600 dark:bg-[#1c2432] dark:text-gray-300'
          }`}
        >
          <PencilIcon className="h-[19px] w-[19px]" />
        </button>
        <button
          type="button"
          onClick={() => setShowAddBookingModal(true)}
          aria-label="Neue Buchung"
          className="flex h-[42px] w-[42px] flex-none items-center justify-center rounded-xl bg-primary-600 text-white shadow-sm hover:bg-primary-700"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Hero Stats - kompakt: alle Kennzahlen in einer Card */}
      {accounts.length > 0 && (
        <div className="bg-white dark:bg-[#1c2432] rounded-2xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5 mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 items-start gap-2 sm:items-center sm:gap-6">
            <StatCard
              icon={BanknotesIcon}
              tone="primary"
              label="Kontostand gesamt"
              value={formatCurrency(totalBalance)}
              sub={
                <span className="inline-flex items-center gap-1">
                  {saldo >= 0 ? (
                    <ArrowTrendingUpIcon className="h-3.5 w-3.5 text-success-500" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-3.5 w-3.5 text-error-500" />
                  )}
                  <span className={`font-semibold tabular-nums ${saldo >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                    {saldo >= 0 ? '+' : ''}{formatCurrency(saldo)}
                  </span>
                  <span>Jahressaldo {selectedYear}</span>
                </span>
              }
            />

            <StatCard
              icon={ArrowTrendingUpIcon}
              tone="success"
              valueTone="success"
              label="Einnahmen"
              value={formatCurrency(totalIncome)}
            />

            <StatCard
              icon={ArrowTrendingDownIcon}
              tone="error"
              valueTone="error"
              label="Ausgaben"
              value={formatCurrency(totalExpense)}
            />

            {(() => {
              const card = (
                <StatCard
                  icon={uncategorizedCount === 0 ? CheckCircleIcon : ExclamationTriangleIcon}
                  tone={uncategorizedCount === 0 ? 'success' : 'warning'}
                  valueTone={uncategorizedCount === 0 ? 'success' : 'warning'}
                  label="Unkategorisiert"
                  value={uncategorizedCount}
                  sub={uncategorizedCount === 0 ? 'Alle Buchungen kategorisiert' : 'Buchungen benötigen Zuordnung'}
                />
              );
              return uncategorizedCount > 0 ? (
                <button
                  type="button"
                  onClick={() => handleCategoryClick('Unkategorisiert')}
                  title="Unkategorisierte Buchungen anzeigen"
                  className="-m-1 rounded-lg p-1 text-left transition-colors hover:bg-warning-50 dark:hover:bg-warning-900/10 focus:outline-none focus:ring-2 focus:ring-warning-500/40"
                >
                  {card}
                </button>
              ) : card;
            })()}
          </div>

          {/* Balken: Einnahmen vs Ausgaben */}
          {(totalIncome > 0 || totalExpense > 0) && (
            <div className="mt-4 h-2.5 rounded-full bg-gray-100 dark:bg-gray-700/50 overflow-hidden flex">
              <div
                className="h-full bg-success-500 rounded-l-full transition-all duration-500"
                style={{ width: `${(totalIncome / (totalIncome + totalExpense)) * 100}%` }}
              />
              <div
                className="h-full bg-error-400 rounded-r-full transition-all duration-500"
                style={{ width: `${(totalExpense / (totalIncome + totalExpense)) * 100}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Accounts Overview or Welcome Screen */}
      {accounts.length === 0 && !isLoadingAccounts ? (
        <div className="bg-white dark:bg-[#1c2432] rounded-2xl border border-gray-200 dark:border-gray-700 p-8 sm:p-12 mb-6">
          <div className="max-w-2xl mx-auto text-center">
            <BuildingLibraryIcon className="h-16 w-16 text-primary-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Willkommen bei der Finanzverwaltung
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Verwalten Sie alle Ihre Konten, Buchungen und Finanzen übersichtlich an einem Ort.
            </p>

            <div className="bg-primary-50 dark:bg-primary-900/20 rounded-xl p-8 mb-8 text-left border border-primary-200 dark:border-primary-700/30">
              <h3 className="text-base font-semibold text-primary-900 dark:text-primary-100 mb-6 flex items-center gap-2">
                <ChartPieIcon className="h-6 w-6" />
                So funktioniert die Finanzverwaltung:
              </h3>
              <div className="space-y-4">
                <div className="flex gap-4 bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    1
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white mb-1 text-base flex items-center gap-2">
                      <WalletIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      Konten anlegen
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      Erstellen Sie verschiedene Konten wie Girokonto, Bargeldkasse oder Sparkonto zur Verwaltung Ihrer Finanzen.
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">
                      <strong>Kontotypen:</strong> Bankkonto, Bargeldkonto, Kreditkarte, Depot, Sonstiges
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    2
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white mb-1 text-base flex items-center gap-2">
                      <DocumentIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      Kontoauszüge importieren
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      Importieren Sie Ihre Kontoauszüge per CSV-Import. Unterstützt werden verschiedene Bankformate.
                    </p>
                    <div className="bg-info-50 dark:bg-info-900/20 rounded-md p-2 border border-info-200 dark:border-info-700/30 mt-2">
                      <ul className="text-xs text-info-700 dark:text-info-300 space-y-1">
                        <li>• Automatische Kategorisierung</li>
                        <li>• Erkennung von Duplikaten</li>
                        <li>• Unterstützung mehrerer Bankformate</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    3
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white mb-1 text-base flex items-center gap-2">
                      <PlusIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      Buchungen erfassen
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      Erfassen Sie manuelle Buchungen für Bargeldtransaktionen oder andere Einnahmen und Ausgaben.
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">
                      <strong>Tipp:</strong> Nutzen Sie den "Neue Buchung" Button für schnelle Erfassung.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    4
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white mb-1 text-base flex items-center gap-2">
                      <TagIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      Kategorien zuordnen
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      Ordnen Sie Ihre Buchungen Kategorien zu für eine bessere Übersicht über Ihre Ausgaben und Einnahmen.
                    </p>
                    <div className="bg-primary-50 dark:bg-primary-900/20 rounded-md p-2 border border-primary-200 dark:border-primary-700/30 mt-2">
                      <ul className="text-xs text-primary-700 dark:text-primary-300 space-y-1">
                        <li>• Detaillierte Ausgabenanalyse</li>
                        <li>• Jahresübersichten</li>
                        <li>• Vergleich von Einnahmen und Ausgaben</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    5
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white mb-1 text-base flex items-center gap-2">
                      <DocumentArrowDownIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      Berichte exportieren
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      Exportieren Sie Ihre Finanzdaten als Excel-Datei für weitere Analysen oder die Steuererklärung.
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">
                      <strong>Formate:</strong> Excel (.xlsx) mit mehreren Arbeitsblättern
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setIsEditMode(true);
                setShowCreateAccountModal(true);
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium mx-auto"
            >
              <WalletIcon className="h-5 w-5" />
              Erstes Konto anlegen
            </button>
          </div>
        </div>
      ) : (
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Konten
        </h2>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4 max-w-[1600px]">
          {isLoadingAccounts ? (
            <div className="col-span-full text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Lade Konten...
              </p>
            </div>
          ) : (
            accounts
              .filter((account) => {
                // Zeige alle Konten außer deaktivierte (es sei denn, wir sind im Edit-Modus)
                if (!isEditMode && account.deactivated) return false;
                return true; // Zeige alle aktiven Konten, auch ohne Buchungen
              })
              .map((account) => {
                const { IconComponent, colorName } = getAccountIconDetails(account);
                const isDeactivated = account.deactivated === 1;

                return (
                <div
                  key={account.id}
                  onClick={() => !isDeactivated && handleAccountClick(account)}
                  className={`${isDeactivated ? 'bg-gray-50 dark:bg-gray-800/70 opacity-60' : 'bg-white dark:bg-[#1c2432]'} rounded-2xl p-4 border border-gray-200 dark:border-gray-700 ${!isDeactivated ? 'hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer hover:shadow-md' : ''} transition-all relative`}
                >
                  {isEditMode && (
                    <>
                      <button
                        title="Konto bearbeiten"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(account);
                        }}
                        className="absolute top-3 right-14 p-1.5 rounded-md text-xs font-medium text-white shadow-md hover:shadow-lg transition-all duration-200 bg-info-600 hover:bg-info-700"
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                      </button>
                      <button
                        title={isDeactivated ? 'Konto aktivieren' : (account.currentBalance === 0 || account.currentBalance === account.startbetrag) ? 'Konto löschen' : 'Konto deaktivieren'}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isDeactivated) {
                            setConfirmModal({
                              isOpen: true,
                              type: 'question',
                              title: 'Konto aktivieren',
                              message: `Soll das Konto "${account.name}" wieder aktiviert werden?`,
                              variant: 'two',
                              onConfirm: () => handleActivateAccount(account.id)
                            });
                          } else {
                            const isDeletable = account.currentBalance === 0 || account.currentBalance === account.startbetrag;
                            setConfirmModal({
                              isOpen: true,
                              type: 'warning',
                              title: isDeletable ? 'Konto löschen' : 'Konto deaktivieren',
                              message: isDeletable
                                ? `Soll das Konto "${account.name}" wirklich dauerhaft gelöscht werden?`
                                : `Soll das Konto "${account.name}" deaktiviert werden?`,
                              variant: 'two',
                              onConfirm: () => isDeletable ? handleDeleteAccount(account.id) : handleDeactivateAccount(account.id)
                            });
                          }
                        }}
                        className={`absolute top-3 right-3 p-1.5 rounded-md text-xs font-medium text-white shadow-md hover:shadow-lg transition-all duration-200 ${isDeactivated ? 'bg-success-600 hover:bg-success-700' : 'bg-error-600 hover:bg-error-700'}`}
                      >
                        {isDeactivated ? (
                          <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                        ) : (
                          (account.currentBalance === 0 || account.currentBalance === account.startbetrag) ? (
                            <TrashIcon className="h-3.5 w-3.5" />
                          ) : (
                            <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                          )
                        )}
                      </button>
                    </>
                  )}
                  <div className="flex flex-col h-full">
                    {/* Kopfzeile: Icon, Name/Untertitel, Saldo rechts */}
                    <div className={`flex items-center gap-3 ${isEditMode ? 'pr-16' : ''}`}>
                      <div className={`w-9 h-9 flex-shrink-0 ${isDeactivated ? 'bg-gray-200 dark:bg-gray-700' : 'bg-primary-100 dark:bg-primary-900/20'} rounded-full flex items-center justify-center ${isDeactivated ? 'text-gray-400' : 'text-primary-600'}`}>
                        <IconComponent className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-base font-semibold truncate ${isDeactivated ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                          {account.name}
                        </h3>
                        {(() => {
                          const subtitle = [
                            account.kontotyp !== account.name ? account.kontotyp : null,
                            account.beschreibung || null,
                          ].filter(Boolean).join(' · ');
                          return subtitle ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>
                          ) : null;
                        })()}
                      </div>
                      <span className={`text-lg font-bold tabular-nums flex-shrink-0 ${
                        isDeactivated ? 'text-gray-500' : (
                          (account.currentBalance || 0) >= 0
                            ? 'text-success-600 dark:text-success-400'
                            : 'text-error-600 dark:text-error-400'
                        )
                      }`}>
                        {formatCurrency(account.currentBalance || 0)}
                      </span>
                    </div>

                    {/* Badges nur wenn vorhanden */}
                    {(account.ist_gaestekarten_konto === 1 || account.ist_verzehrkosten_konto === 1) && (
                      <div className="flex gap-1 mt-2">
                        {account.ist_gaestekarten_konto === 1 && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-info-100 text-info-800 dark:bg-info-900 dark:text-info-200">
                            <CreditCardIcon className="h-3 w-3 mr-0.5" />
                            Gästekarten
                          </span>
                        )}
                        {account.ist_verzehrkosten_konto === 1 && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                            <ShoppingCartIcon className="h-3 w-3 mr-0.5" />
                            Verzehr
                          </span>
                        )}
                      </div>
                    )}

                    {/* Recent Transactions - kompakt */}
                    {!isDeactivated && (
                      <div className="border-t border-gray-200 dark:border-gray-700 mt-3 pt-2.5">
                        <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">
                          Letzte Buchungen
                        </p>
                        {account.recentTransactions && account.recentTransactions.length > 0 ? (
                          <div className="space-y-1">
                            {account.recentTransactions.slice(0, 3).map((txn, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between text-xs"
                              >
                                <span
                                  className="text-gray-600 dark:text-gray-400 truncate max-w-[60%]"
                                  title={zweckKurz(txn.verwendungszweck) || undefined}
                                >
                                  {zahlungsbeteiligter(txn)}
                                </span>
                                <span className={`font-semibold tabular-nums ${
                                  txn.betrag >= 0
                                    ? 'text-success-600 dark:text-success-400'
                                    : 'text-error-600 dark:text-error-400'
                                }`}>
                                  {txn.betrag >= 0 ? '+' : ''}{formatCurrency(txn.betrag)}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            Keine Buchungen vorhanden
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
          
          {/* New Account Creation Card - Only in Edit Mode */}
          {isEditMode && (
            <div
              className="bg-gray-50 dark:bg-[#1c2432] rounded-2xl p-5 border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-primary-500 dark:hover:border-primary-400 hover:shadow-md cursor-pointer group relative transition-all duration-200 flex items-center justify-center min-h-[240px]"
              onClick={() => setShowCreateAccountModal(true)}
            >
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-primary-100 dark:bg-primary-900/30 rounded-2xl">
                    <PlusIcon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-300 mb-2">
                  Neues Konto erstellen
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Konto hinzufügen und verwalten
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Hint for missing default accounts */}
      {accounts.length > 0 && (() => {
        const hasGaestekartenKonto = accounts.some(acc => acc.ist_gaestekarten_konto === 1);
        const hasVerzehrkostenKonto = accounts.some(acc => acc.ist_verzehrkosten_konto === 1);

        if (!hasGaestekartenKonto || !hasVerzehrkostenKonto) {
          return (
            <div className="mb-6 p-4 bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-info-600 dark:text-info-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-info-900 dark:text-info-100 mb-1">
                    Standard-Konten fehlen
                  </h4>
                  <p className="text-sm text-info-800 dark:text-info-200 mb-2">
                    {!hasGaestekartenKonto && !hasVerzehrkostenKonto ? (
                      <>Es wurde noch kein Standard-Konto für <strong>Gästekarten</strong> und <strong>Verzehrkosten</strong> festgelegt.</>
                    ) : !hasGaestekartenKonto ? (
                      <>Es wurde noch kein Standard-Konto für <strong>Gästekarten</strong> festgelegt.</>
                    ) : (
                      <>Es wurde noch kein Standard-Konto für <strong>Verzehrkosten</strong> festgelegt.</>
                    )}
                  </p>
                  <div className="text-xs text-info-700 dark:text-info-300 space-y-1">
                    <p className="font-medium">So legen Sie Standard-Konten fest:</p>
                    <ol className="list-decimal list-inside space-y-0.5 ml-2">
                      <li>Klicken Sie auf <strong>"Verwalten"</strong> in der oberen Leiste</li>
                      <li>Klicken Sie auf das <strong>blaue Stift-Icon</strong> beim gewünschten Konto</li>
                      <li>Aktivieren Sie die Checkbox für "Gästekarten" oder "Verzehrkosten"</li>
                      <li>Klicken Sie auf <strong>"Speichern"</strong></li>
                    </ol>
                    <p className="mt-2 italic">
                      Diese Konten werden automatisch für die jeweiligen Transaktionen verwendet.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-error-100 dark:bg-error-900/20 text-error-700 dark:text-error-400 rounded-lg flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5" />
          {error}
        </div>
      )}

      {/* Rücklagen Card */}
      <div className="mb-6">
        <RuecklagenCard isVorstand={user?.vorstand === 1} />
      </div>

      {/* Categories - Only show when accounts exist */}
      {accounts.length > 0 && (
        <div className="bg-white dark:bg-[#1c2432] rounded-2xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <TagIcon className="h-4.5 w-4.5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900 dark:text-white">
                  Kategorien
                </h2>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {sortedCategories.length} Kategorien · {selectedYear}
                </p>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-xs">
              <span className="flex items-center gap-1 px-2 py-1 bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-300 rounded-md font-medium border border-success-200 dark:border-success-800">
                <ArrowUpIcon className="h-3 w-3" />
                {formatCurrency(totalIncome)}
              </span>
              <span className="flex items-center gap-1 px-2 py-1 bg-error-50 dark:bg-error-900/20 text-error-700 dark:text-error-300 rounded-md font-medium border border-error-200 dark:border-error-800">
                <ArrowDownIcon className="h-3 w-3" />
                {formatCurrency(totalExpense)}
              </span>
            </div>
          </div>

          {isLoadingTransactions && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-200 border-t-primary-600"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">Lade Kategorien...</p>
            </div>
          )}

          {/* Categories Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Income Categories */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-success-500"></div>
                <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Einnahmen</span>
                <div className="flex-1 h-px bg-gray-100 dark:bg-gray-700/50"></div>
              </div>
              <div className="space-y-1">
                {sortedCategories.filter((cat) => cat.income > cat.expense).length > 0 ? (
                  sortedCategories
                    .filter((cat) => cat.income > cat.expense)
                    .map((cat) => {
                      const bookingMonths = categoryTransactions[cat.category] || [];
                      const CategoryIcon = getCategoryIcon(cat.icon);
                      const percent = totalIncome > 0 ? (cat.income / totalIncome) * 100 : 0;
                      const maxAmount = Math.max(...sortedCategories.filter(c => c.income > c.expense).map(c => c.income));
                      const barWidth = maxAmount > 0 ? (cat.income / maxAmount) * 100 : 0;

                      return (
                        <div
                          key={cat.category}
                          onClick={() => handleCategoryClick(cat.category)}
                          className="group cursor-pointer rounded-xl p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-success-100 dark:bg-success-900/30 flex-shrink-0">
                                <CategoryIcon className="h-4 w-4 text-success-600 dark:text-success-400" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-success-600 dark:group-hover:text-success-400 transition-colors">
                                  {cat.category || 'Unkategorisiert'}
                                </p>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    {cat.transactionCount} Buchungen
                                  </span>
                                  <div className="hidden sm:flex gap-0.5">
                                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(month => (
                                      <div
                                        key={month}
                                        className={`w-1 h-1 rounded-full ${
                                          bookingMonths.includes(month)
                                            ? 'bg-success-500'
                                            : 'bg-gray-200 dark:bg-gray-600'
                                        }`}
                                        title={['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'][month-1]}
                                      />
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0 ml-3">
                              <p className="text-sm font-bold text-success-600 dark:text-success-400 tabular-nums">
                                +{formatCurrency(cat.income)}
                              </p>
                              <p className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
                                {percent.toFixed(0)}%
                              </p>
                            </div>
                          </div>
                          <div className="ml-[42px] h-1.5 rounded-full bg-gray-100 dark:bg-gray-700/50 overflow-hidden">
                            <div
                              className="h-full bg-success-500/70 rounded-full transition-all duration-300"
                              style={{ width: `${barWidth}%` }}
                            />
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                    <ArrowUpIcon className="h-6 w-6 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Keine Einnahmen</p>
                  </div>
                )}
              </div>
            </div>

            {/* Expense Categories */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-error-500"></div>
                <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Ausgaben</span>
                <div className="flex-1 h-px bg-gray-100 dark:bg-gray-700/50"></div>
              </div>
              <div className="space-y-1">
                {sortedCategories.filter((cat) => cat.expense >= cat.income && cat.expense > 0).length > 0 ? (
                  sortedCategories
                    .filter((cat) => cat.expense >= cat.income && cat.expense > 0)
                    .map((cat) => {
                      const CategoryIcon = getCategoryIcon(cat.icon);
                      const bookingMonths = categoryTransactions[cat.category] || [];
                      const percent = totalExpense > 0 ? (cat.expense / totalExpense) * 100 : 0;
                      const maxAmount = Math.max(...sortedCategories.filter(c => c.expense >= c.income && c.expense > 0).map(c => c.expense));
                      const barWidth = maxAmount > 0 ? (cat.expense / maxAmount) * 100 : 0;

                      return (
                        <div
                          key={cat.category}
                          onClick={() => handleCategoryClick(cat.category)}
                          className="group cursor-pointer rounded-xl p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-error-100 dark:bg-error-900/30 flex-shrink-0">
                                <CategoryIcon className="h-4 w-4 text-error-600 dark:text-error-400" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-error-600 dark:group-hover:text-error-400 transition-colors">
                                  {cat.category || 'Unkategorisiert'}
                                </p>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    {cat.transactionCount} Buchungen
                                  </span>
                                  <div className="hidden sm:flex gap-0.5">
                                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(month => (
                                      <div
                                        key={month}
                                        className={`w-1 h-1 rounded-full ${
                                          bookingMonths.includes(month)
                                            ? 'bg-error-500'
                                            : 'bg-gray-200 dark:bg-gray-600'
                                        }`}
                                        title={['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'][month-1]}
                                      />
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0 ml-3">
                              <p className="text-sm font-bold text-error-600 dark:text-error-400 tabular-nums">
                                -{formatCurrency(cat.expense)}
                              </p>
                              <p className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
                                {percent.toFixed(0)}%
                              </p>
                            </div>
                          </div>
                          <div className="ml-[42px] h-1.5 rounded-full bg-gray-100 dark:bg-gray-700/50 overflow-hidden">
                            <div
                              className="h-full bg-error-400/70 rounded-full transition-all duration-300"
                              style={{ width: `${barWidth}%` }}
                            />
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                    <ArrowDownIcon className="h-6 w-6 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Keine Ausgaben</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Account Transaction Modal */}
      {selectedAccount && (
        <ModalShell
          open={showAccountModal}
          onClose={() => {
            setShowAccountModal(false);
            setSelectedAccount(null);
            setTransactions([]);
            setAccountDetailTab('transactions');
            setAccountStatementCount(null);
          }}
          variant="detail"
          size="xl"
          icon={accountIconMap[selectedAccount.icon] || WalletIcon}
          title={selectedAccount.name}
          subtitle={selectedAccount.beschreibung}
          footer={
            <ModalActions
              secondary={{
                label: 'Schließen',
                onClick: () => {
                  setShowAccountModal(false);
                  setSelectedAccount(null);
                  setTransactions([]);
                  setAccountDetailTab('transactions');
                  setAccountStatementCount(null);
                },
              }}
            />
          }
        >
          {/* Saldo-Panel (Design: „Kontoansicht Modal") */}
          <ModalSection className="mb-4">
            {(() => {
              const income = transactions
                .filter((t) => t.betrag >= 0)
                .reduce((s, t) => s + t.betrag, 0);
              const expense = transactions
                .filter((t) => t.betrag < 0)
                .reduce((s, t) => s + Math.abs(t.betrag), 0);
              const balance = selectedAccount.currentBalance || 0;
              const years = Array.from(
                new Set(
                  transactions
                    .map((t) => {
                      const d = new Date(t.buchungstag);
                      return isNaN(d.getTime()) ? null : d.getFullYear();
                    })
                    .filter((y): y is number => y !== null),
                ),
              );
              const yearLabel = years.length === 1 ? String(years[0]) : null;
              return (
                <div className="rounded-xl bg-gray-50 dark:bg-[#0f1419] p-4 sm:p-5 border border-gray-200 dark:border-gray-700/50">
                  <div className="flex items-end justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Aktueller Saldo
                      </div>
                      <div
                        className={`font-mono tabular-nums text-3xl sm:text-4xl font-bold tracking-tight mt-1.5 ${
                          balance >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'
                        }`}
                      >
                        {formatCurrency(balance)}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {yearLabel && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-200">
                          <CalendarIcon className="h-3.5 w-3.5" />
                          {yearLabel}
                        </span>
                      )}
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                        {transactions.length} {transactions.length === 1 ? 'Buchung' : 'Buchungen'}
                      </div>
                    </div>
                  </div>

                  {transactions.length > 0 && (
                    <div className="flex flex-col sm:flex-row gap-2.5 mt-4">
                      <div className="flex-1 flex items-center gap-2.5 px-3.5 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <span className="flex-none w-7 h-7 rounded-full bg-success-100 dark:bg-success-900/30 text-success-600 dark:text-success-400 flex items-center justify-center">
                          <ArrowUpIcon className="h-4 w-4" strokeWidth={2.4} />
                        </span>
                        <div className="min-w-0">
                          <div className="text-xs text-gray-500 dark:text-gray-400">Eingänge</div>
                          <div className="font-mono tabular-nums text-sm font-semibold text-success-600 dark:text-success-400">
                            {formatCurrency(income)}
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 flex items-center gap-2.5 px-3.5 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <span className="flex-none w-7 h-7 rounded-full bg-error-100 dark:bg-error-900/30 text-error-600 dark:text-error-400 flex items-center justify-center">
                          <ArrowDownIcon className="h-4 w-4" strokeWidth={2.4} />
                        </span>
                        <div className="min-w-0">
                          <div className="text-xs text-gray-500 dark:text-gray-400">Ausgänge</div>
                          <div className="font-mono tabular-nums text-sm font-semibold text-error-600 dark:text-error-400">
                            {formatCurrency(expense)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </ModalSection>

          <div
            role="tablist"
            aria-label="Kontoinhalte"
            className="mb-4 grid grid-cols-2 rounded-xl border border-gray-200 bg-gray-100 p-1 dark:border-gray-700 dark:bg-[#0f1419]"
          >
            <button
              type="button"
              id="account-tab-transactions"
              role="tab"
              aria-selected={accountDetailTab === 'transactions'}
              aria-controls="account-panel-transactions"
              tabIndex={accountDetailTab === 'transactions' ? 0 : -1}
              onClick={() => setAccountDetailTab('transactions')}
              onKeyDown={(event) => {
                if (event.key === 'ArrowRight') setAccountDetailTab('statements');
              }}
              className={`flex min-h-11 items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition-all ${
                accountDetailTab === 'transactions'
                  ? 'bg-white text-primary-700 shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:text-primary-300 dark:ring-gray-700'
                  : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <BanknotesIcon className="h-[18px] w-[18px]" />
              <span>Buchungen</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums ${
                accountDetailTab === 'transactions'
                  ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200'
                  : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}>
                {transactions.length}
              </span>
            </button>
            <button
              type="button"
              id="account-tab-statements"
              role="tab"
              aria-selected={accountDetailTab === 'statements'}
              aria-controls="account-panel-statements"
              tabIndex={accountDetailTab === 'statements' ? 0 : -1}
              onClick={() => setAccountDetailTab('statements')}
              onKeyDown={(event) => {
                if (event.key === 'ArrowLeft') setAccountDetailTab('transactions');
              }}
              className={`flex min-h-11 items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition-all ${
                accountDetailTab === 'statements'
                  ? 'bg-white text-primary-700 shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:text-primary-300 dark:ring-gray-700'
                  : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <DocumentTextIcon className="h-[18px] w-[18px]" />
              <span>Kontoauszüge</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums ${
                accountDetailTab === 'statements'
                  ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200'
                  : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}>
                {accountStatementCount ?? '…'}
              </span>
            </button>
          </div>

          <div
            id="account-panel-statements"
            role="tabpanel"
            aria-labelledby="account-tab-statements"
            hidden={accountDetailTab !== 'statements'}
          >
            <BankStatementManager
              accountId={selectedAccount.id}
              accountName={selectedAccount.name}
              currency={selectedAccount.waehrung || 'EUR'}
              onCountChange={setAccountStatementCount}
            />
          </div>

          <div
            id="account-panel-transactions"
            role="tabpanel"
            aria-labelledby="account-tab-transactions"
            hidden={accountDetailTab !== 'transactions'}
          >
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">Lade Buchungen...</p>
            </div>
          ) : transactions.length > 0 ? (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700/50 overflow-hidden divide-y divide-gray-200 dark:divide-gray-700/50">
              {transactions.map((txn) => {
                const isIncome = txn.betrag >= 0;
                return (
                  <div
                    key={txn.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedStatementId(String(txn.id))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedStatementId(String(txn.id));
                      }
                    }}
                    className="cursor-pointer flex items-center gap-3.5 px-3.5 py-3 bg-white dark:bg-transparent hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500/40"
                  >
                    <span
                      className={`flex-none w-[34px] h-[34px] rounded-full flex items-center justify-center ${
                        isIncome
                          ? 'bg-success-100 dark:bg-success-900/30 text-success-600 dark:text-success-400'
                          : 'bg-error-100 dark:bg-error-900/30 text-error-600 dark:text-error-400'
                      }`}
                    >
                      {isIncome ? (
                        <ArrowUpRightIcon className="h-4 w-4" strokeWidth={2.2} />
                      ) : (
                        <ArrowDownRightIcon className="h-4 w-4" strokeWidth={2.2} />
                      )}
                    </span>
                    {(() => {
                      const t = buchungsTexte(txn);
                      return (
                    <div className="min-w-0 flex-1">
                      {/* Überschrift ist der Zahlungsbeteiligte, nicht die Buchungsart — siehe zahlungsbeteiligter() */}
                      <p className="font-medium text-gray-900 dark:text-white truncate">{t.kopf}</p>
                      {t.zweck && (
                        <p
                          className="mt-0.5 truncate text-[12.5px] text-gray-600 dark:text-gray-300"
                          title={txn.verwendungszweck || undefined}
                        >
                          {t.zweck}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        <span className="font-mono tabular-nums">{formatDate(txn.buchungstag)}</span>
                        {/* Die Buchungsart rutscht in die Nebenzeile - sie bleibt nützlich, taugt aber nicht als Überschrift */}
                        {t.art && (
                          <>
                            <span className="w-[3px] h-[3px] rounded-full bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
                            <span className="truncate">{t.art}</span>
                          </>
                        )}
                        {txn.category && (
                          <>
                            <span className="w-[3px] h-[3px] rounded-full bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
                            <span className="font-medium text-gray-600 dark:text-gray-300 truncate">
                              {txn.category}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                      );
                    })()}
                    <div
                      className={`font-mono tabular-nums font-bold text-base flex-shrink-0 whitespace-nowrap ${
                        isIncome
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-error-600 dark:text-error-400'
                      }`}
                    >
                      {isIncome ? '+' : '−'}{formatCurrency(Math.abs(txn.betrag))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
                <WalletIcon className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-500 dark:text-gray-400">Keine Buchungen für dieses Konto gefunden.</p>
            </div>
          )}
          </div>
        </ModalShell>
      )}

      <UncategorizedReviewModal
        open={showTransactionModal && selectedCategory === 'Unkategorisiert'}
        year={selectedYear}
        transactions={transactions}
        loading={isLoading}
        onClose={() => {
          setShowTransactionModal(false);
          setSelectedCategory(null);
          setTransactions([]);
        }}
        onOpenStatement={(statementId) => setSelectedStatementId(String(statementId))}
        onApplied={async () => {
          await fetchAllData();
          await handleCategoryClick('Unkategorisiert');
        }}
      />

      {/* Category Transaction Modal */}
      {selectedCategory && selectedCategory !== 'Unkategorisiert' && (
        <ModalShell
          open={showTransactionModal}
          onClose={() => {
            setShowTransactionModal(false);
            setSelectedCategory(null);
            setTransactions([]);
          }}
          variant="detail"
          size="xl"
          icon={TagIcon}
          title={selectedCategory || 'Unkategorisiert'}
          subtitle={`${transactions.length} Buchungen im Jahr ${selectedYear}`}
          footer={
            <ModalActions
              secondary={{
                label: 'Schließen',
                onClick: () => {
                  setShowTransactionModal(false);
                  setSelectedCategory(null);
                  setTransactions([]);
                },
              }}
            />
          }
        >
          {/* Summary Stats */}
          {transactions.length > 0 && (() => {
            const totalIncome = transactions.filter(t => t.isIncome).reduce((sum, t) => sum + Math.abs(t.categoryAmount || 0), 0);
            const totalExpense = transactions.filter(t => !t.isIncome).reduce((sum, t) => sum + Math.abs(t.categoryAmount || 0), 0);
            const netAmount = totalIncome - totalExpense;
            return (
              <ModalSection className="mb-4">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-gray-50 dark:bg-[#0f1419] p-4 border border-gray-200 dark:border-gray-700/50">
                  <div className="flex flex-wrap gap-3">
                    {totalIncome > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800/50 rounded-xl">
                        <ArrowUpIcon className="h-4 w-4 text-success-600 dark:text-success-400" />
                        <span className="text-sm font-semibold text-success-700 dark:text-success-300">
                          {formatCurrency(totalIncome)}
                        </span>
                      </div>
                    )}
                    {totalExpense > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800/50 rounded-xl">
                        <ArrowDownIcon className="h-4 w-4 text-error-600 dark:text-error-400" />
                        <span className="text-sm font-semibold text-error-700 dark:text-error-300">
                          {formatCurrency(totalExpense)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold mb-1">Netto</p>
                    <p className={`text-xl font-bold ${netAmount >= 0 ? 'text-success-500' : 'text-error-500'}`}>
                      {netAmount >= 0 ? '+' : ''}{formatCurrency(netAmount)}
                    </p>
                  </div>
                </div>
              </ModalSection>
            );
          })()}

          {/* Transaction List */}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">Lade Buchungen...</p>
            </div>
          ) : transactions.length > 0 ? (
            <div className="space-y-3">
              {transactions.map((txn) => (
                <div
                  key={txn.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedStatementId(String(txn.id))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedStatementId(String(txn.id));
                    }
                  }}
                  className="cursor-pointer bg-gray-50 dark:bg-[#0f1419] rounded-xl p-4 border border-gray-200 dark:border-gray-700/50 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-xl flex-shrink-0 ${
                        txn.isIncome
                          ? 'bg-success-100 dark:bg-success-900/30'
                          : 'bg-error-100 dark:bg-error-900/30'
                      }`}>
                        {txn.isIncome ? (
                          <ArrowUpIcon className="h-4 w-4 text-success-600 dark:text-success-400" />
                        ) : (
                          <ArrowDownIcon className="h-4 w-4 text-error-600 dark:text-error-400" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {buchungsTexte(txn).kopf}
                        </p>
                        {buchungsTexte(txn).zweck && (
                          <p
                            className="mt-0.5 truncate text-[12.5px] text-gray-600 dark:text-gray-300"
                            title={txn.verwendungszweck || undefined}
                          >
                            {buchungsTexte(txn).zweck}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="h-3.5 w-3.5" />
                            {formatDate(txn.buchungstag)}
                          </span>
                          <span className="flex items-center gap-1">
                            <BuildingLibraryIcon className="h-3.5 w-3.5" />
                            {txn.konto_name}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={`font-bold text-base flex-shrink-0 ${
                      txn.isIncome
                        ? 'text-success-600 dark:text-success-400'
                        : 'text-error-600 dark:text-error-400'
                    }`}>
                      {txn.isIncome ? '+' : '-'}{formatCurrency(Math.abs(txn.categoryAmount || 0))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
                <TagIcon className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-500 dark:text-gray-400">Keine Buchungen für diese Kategorie gefunden.</p>
            </div>
          )}
        </ModalShell>
      )}

      {/* Excel Export Modal */}
      <ExcelExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        accounts={accounts}
        allStatements={allStatements}
        isMobile={window.innerWidth < 640}
        isLoading={isLoadingExport}
      />

      {/* CSV Import Modal */}
      {showImportModal && (
        <CSVImporter
          onImportSuccess={() => {
            setShowImportModal(false);
            // Reload accounts and categories after successful import
            fetchAllData();
          }}
          onClose={() => setShowImportModal(false)}
        />
      )}

      {/* Add Booking Modal */}
      <AddBookingModal
        isOpen={showAddBookingModal}
        onClose={() => setShowAddBookingModal(false)}
        onSuccess={() => {
          // Reload data after successful booking
          fetchAllData();
        }}
      />

      {/* Buchungs-Detail-Modal (aus der Kategorie-/Unkategorisiert-Liste) */}
      {selectedStatementId && (
        <StatementDetailModal
          statementId={selectedStatementId}
          onClose={() => setSelectedStatementId(null)}
          onStatementUpdate={() => {
            // Übersicht neu laden und offene Kategorieliste aktualisieren
            fetchAllData();
            if (selectedCategory) handleCategoryClick(selectedCategory);
          }}
          onStatementDelete={() => {
            fetchAllData();
            if (selectedCategory) handleCategoryClick(selectedCategory);
          }}
        />
      )}

      {/* Create Account Modal */}
      <ModalShell
        open={showCreateAccountModal}
        onClose={() => {
          setShowCreateAccountModal(false);
          setNewAccountName('');
          setNewAccountBalance('');
          setNewAccountType('Bargeldkonto');
          setNewAccountIcon('FaWallet');
          setNewAccountIsGaestekarten(false);
          setNewAccountIsVerzehrkosten(false);
        }}
        variant="form"
        size="sm"
        icon={WalletIcon}
        title="Neues Konto erstellen"
        closeOnBackdrop={false}
        footer={
          <ModalActions
            secondary={{
              label: 'Abbrechen',
              onClick: () => {
                setShowCreateAccountModal(false);
                setNewAccountName('');
                setNewAccountBalance('');
                setNewAccountType('Bargeldkonto');
                setNewAccountIcon('FaWallet');
                setNewAccountIsGaestekarten(false);
                setNewAccountIsVerzehrkosten(false);
              },
            }}
            primary={{
              label: 'Erstellen',
              onClick: () => handleCreateAccount({ preventDefault: () => {} } as React.FormEvent),
              disabled: !newAccountName.trim(),
              icon: <PlusIcon className="h-5 w-5" />,
            }}
          />
        }
      >
        <form id="createAccountForm" onSubmit={handleCreateAccount} className="space-y-5">
          <ModalSection>
            <div className="space-y-5">
              <div>
                <AttributeLabel iconKey="name" htmlFor="newAccountName" className="mb-2" textClassName="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Kontoname *
                </AttributeLabel>
                <input
                  id="newAccountName"
                  type="text"
                  value={newAccountName}
                  onChange={(e) => setNewAccountName(e.target.value)}
                  className="w-full px-4 py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="z.B. Girokonto Sparkasse"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                  Icon auswählen
                </label>
                <div className="grid grid-cols-5 sm:grid-cols-6 gap-2">
                  {availableIcons.map(({ id, name, IconComponent }) => (
                    <button
                      type="button"
                      key={id}
                      onClick={() => setNewAccountIcon(id)}
                      title={name}
                      className={`p-2 sm:p-2.5 rounded-xl flex items-center justify-center aspect-square transition-all duration-150 ${
                        newAccountIcon === id
                          ? `bg-primary-500 text-white ring-2 ring-primary-400 shadow-lg`
                          : `bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200`
                      }`}
                    >
                      <IconComponent className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <AttributeLabel iconKey="balance" htmlFor="newAccountBalance" className="mb-2" textClassName="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Startbetrag (€)
                </AttributeLabel>
                <input
                  id="newAccountBalance"
                  type="number"
                  value={newAccountBalance}
                  onChange={(e) => setNewAccountBalance(e.target.value)}
                  className="w-full px-4 py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="0,00"
                  step="0.01"
                />
              </div>

              <div>
                <label htmlFor="newAccountType" className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                  Kontotyp
                </label>
                <select
                  id="newAccountType"
                  value={newAccountType}
                  onChange={(e) => setNewAccountType(e.target.value)}
                  className="w-full px-4 py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="Bargeldkonto">Bargeldkonto</option>
                  <option value="Bankkonto">Bankkonto</option>
                  <option value="Kreditkarte">Kreditkarte</option>
                  <option value="Depot">Depot</option>
                  <option value="Sonstiges">Sonstiges</option>
                </select>
              </div>
            </div>
          </ModalSection>

          <ModalSection title="Standard-Konten festlegen" className="mt-5 border-t border-gray-200 dark:border-gray-700/50 pt-5">
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 p-3 rounded-xl transition-colors">
                <input
                  type="checkbox"
                  checked={newAccountIsGaestekarten}
                  onChange={(e) => setNewAccountIsGaestekarten(e.target.checked)}
                  className="w-5 h-5 text-primary-600 bg-gray-100 dark:bg-gray-700 border-0 rounded-lg focus:ring-primary-500 focus:ring-2"
                />
                <div className="flex items-center gap-2">
                  <CreditCardIcon className="h-4 w-4 text-info-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Als Standard für Gästekarten-Transaktionen
                  </span>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 p-3 rounded-xl transition-colors">
                <input
                  type="checkbox"
                  checked={newAccountIsVerzehrkosten}
                  onChange={(e) => setNewAccountIsVerzehrkosten(e.target.checked)}
                  className="w-5 h-5 text-primary-600 bg-gray-100 dark:bg-gray-700 border-0 rounded-lg focus:ring-primary-500 focus:ring-2"
                />
                <div className="flex items-center gap-2">
                  <ShoppingCartIcon className="h-4 w-4 text-warning-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Als Standard für Verzehrkosten-Transaktionen
                  </span>
                </div>
              </label>
            </div>
          </ModalSection>

          {/* Versteckter Submit-Button: erlaubt Absenden per Enter im Formular */}
          <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
        </form>
      </ModalShell>

      {/* Edit Account Modal */}
      {editingAccount && (
        <ModalShell
          open={showEditAccountModal}
          onClose={() => {
            setShowEditAccountModal(false);
            setEditingAccount(null);
            setNewAccountName('');
            setNewAccountType('Bargeldkonto');
            setNewAccountIcon('FaWallet');
            setNewAccountIsGaestekarten(false);
            setNewAccountIsVerzehrkosten(false);
          }}
          variant="form"
          size="sm"
          icon={PencilIcon}
          title="Konto bearbeiten"
          closeOnBackdrop={false}
          footer={
            <ModalActions
              secondary={{
                label: 'Abbrechen',
                onClick: () => {
                  setShowEditAccountModal(false);
                  setEditingAccount(null);
                  setNewAccountName('');
                  setNewAccountType('Bargeldkonto');
                  setNewAccountIcon('FaWallet');
                  setNewAccountIsGaestekarten(false);
                  setNewAccountIsVerzehrkosten(false);
                },
              }}
              primary={{
                label: 'Speichern',
                onClick: () => handleEditAccount({ preventDefault: () => {} } as React.FormEvent),
                disabled: !newAccountName.trim(),
                icon: <PencilIcon className="h-5 w-5" />,
              }}
            />
          }
        >
          <form id="editAccountForm" onSubmit={handleEditAccount} className="space-y-5">
            <ModalSection>
              <div className="space-y-5">
                <div>
                  <AttributeLabel iconKey="name" htmlFor="editAccountName" className="mb-2" textClassName="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Kontoname *
                  </AttributeLabel>
                  <input
                    id="editAccountName"
                    type="text"
                    value={newAccountName}
                    onChange={(e) => setNewAccountName(e.target.value)}
                    className="w-full px-4 py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="z.B. Girokonto Sparkasse"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    Icon auswählen
                  </label>
                  <div className="grid grid-cols-5 sm:grid-cols-6 gap-2">
                    {availableIcons.map(({ id, name, IconComponent }) => (
                      <button
                        type="button"
                        key={id}
                        onClick={() => setNewAccountIcon(id)}
                        title={name}
                        className={`p-2 sm:p-2.5 rounded-xl flex items-center justify-center aspect-square transition-all duration-150 ${
                          newAccountIcon === id
                            ? `bg-primary-500 text-white ring-2 ring-primary-400 shadow-lg`
                            : `bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200`
                        }`}
                      >
                        <IconComponent className="h-5 w-5 sm:h-6 sm:w-6" />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="editAccountType" className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    Kontotyp
                  </label>
                  <select
                    id="editAccountType"
                    value={newAccountType}
                    onChange={(e) => setNewAccountType(e.target.value)}
                    className="w-full px-4 py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="Bargeldkonto">Bargeldkonto</option>
                    <option value="Bankkonto">Bankkonto</option>
                    <option value="Kreditkarte">Kreditkarte</option>
                    <option value="Depot">Depot</option>
                    <option value="Sonstiges">Sonstiges</option>
                  </select>
                </div>
              </div>
            </ModalSection>

            <ModalSection title="Standard-Konten festlegen" className="mt-5 border-t border-gray-200 dark:border-gray-700/50 pt-5">
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 p-3 rounded-xl transition-colors">
                  <input
                    type="checkbox"
                    checked={newAccountIsGaestekarten}
                    onChange={(e) => setNewAccountIsGaestekarten(e.target.checked)}
                    className="w-5 h-5 text-primary-600 bg-gray-100 dark:bg-gray-700 border-0 rounded-lg focus:ring-primary-500 focus:ring-2"
                  />
                  <div className="flex items-center gap-2">
                    <CreditCardIcon className="h-4 w-4 text-info-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Als Standard für Gästekarten-Transaktionen
                    </span>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 p-3 rounded-xl transition-colors">
                  <input
                    type="checkbox"
                    checked={newAccountIsVerzehrkosten}
                    onChange={(e) => setNewAccountIsVerzehrkosten(e.target.checked)}
                    className="w-5 h-5 text-primary-600 bg-gray-100 dark:bg-gray-700 border-0 rounded-lg focus:ring-primary-500 focus:ring-2"
                  />
                  <div className="flex items-center gap-2">
                    <ShoppingCartIcon className="h-4 w-4 text-warning-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Als Standard für Verzehrkosten-Transaktionen
                    </span>
                  </div>
                </label>
              </div>
            </ModalSection>

            {/* Versteckter Submit-Button: erlaubt Absenden per Enter im Formular */}
            <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
          </form>
        </ModalShell>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => {
            confirmModal.onConfirm?.();
            setConfirmModal({ ...confirmModal, isOpen: false });
          }
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
          },
          confirm: {
            text: 'Bestätigen',
            onPress: () => {
              confirmModal.onConfirm?.();
              setConfirmModal({ ...confirmModal, isOpen: false });
            }
          }
        } : undefined}
      />
      </div>
    </>
  );
};

export default AccountOverviewEnhanced;
